import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { MFAContext } from './context';
import _ from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { postRequest } from '@config/axios/axios.config';
import { paths } from '@common/types';
import { toast } from 'react-toastify';
import { HttpErrorMessage } from '../common';
import { OTPInput } from '@components';
import useUserStore from 'store/user.store';
import { useNavigate } from 'react-router-dom';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { formatApiError } from '@common/errors/error-formatting.ts';

export const MFAPhone = () => {
  const { goToPreviousStep } = useContext(MFAContext);
  const [error, setError] = useState<string>();
  const [mfaPhoneNumber, setMFAPhoneNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [otpValue, setOTPValue] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);

  const [showSendCode, setShowSendCode] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { registerToken } = useUserStore();

  const navigate = useNavigate();

  const handleReturnToLogin = () => {
    navigate('/auth/login', { replace: true });
  };

  const handleOnChangeOtpValue = (newValue: string) => {
    setOTPValue(newValue);
  };

  const handleChangePhoneNumber = (newNumber: string) => {
    setMFAPhoneNumber(newNumber);
    const isValid = isValidPhoneNumber(newNumber, 'US');
    if (!isValid) {
      setError('Must match a valid phone number format');
    } else {
      setError(undefined);
    }
  };

  const registerMFARequest = async () => {
    setLoading(true);
    try {
      await postRequest<
        paths['/auth/register/mfa']['post']['requestBody']['content']['application/json'],
        paths['/auth/register/mfa']['post']['responses']['201']['content']['application/json']
      >('/auth/register/mfa', {
        token: registerToken,
        type: 'sms',
        mobile_phone_number: mfaPhoneNumber,
      });

      toast.success('Code sent successfully');
      setShowSendCode(true);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = () => {
    registerMFARequest();
  };

  const resendMFARequest = async () => {
    try {
      await postRequest<
        paths['/auth/register/mfa/resend']['post']['requestBody']['content']['application/json'],
        paths['/auth/register/mfa/resend']['post']['responses']['200']['content']['application/json']
      >('/auth/register/mfa/resend', {
        token: registerToken,
      });

      toast.success('Code sent successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleResendCode = () => {
    resendMFARequest();
  };

  useEffect(() => {
    async function fetchMyAPI() {
      try {
        setHasError(false);

        await postRequest<
          paths['/auth/register/mfa/confirm']['post']['requestBody']['content']['application/json'],
          paths['/auth/register/mfa/confirm']['post']['responses']['200']['content']['application/json']
        >('/auth/register/mfa/confirm', {
          token: registerToken,
          secret: otpValue,
        });

        queryClient.invalidateQueries({ queryKey: ['profile'] });

        toast.success('MFA setup successful');
        handleReturnToLogin();
      } catch {
        setHasError(true);
      }
    }

    if (otpValue.length === 6 && !otpValue.includes(' ')) {
      fetchMyAPI();
    }
  }, [otpValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3.5rem',
        width: '28rem',
        height: '100%',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingX: '1.5rem',
          paddingY: '1rem',
          gap: '32px',
          width: '100%',
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: 0,
          }}
        >
          <VerifiedUserIcon sx={{ fontSize: '3rem' }} color='primary' />
          <Typography
            sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
          >
            Protect your account with MFA
          </Typography>
        </Box>

        <Box sx={{ width: '100%' }}>
          {showSendCode ? (
            <>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'regular',
                  textAlign: 'center',
                  marginBottom: 4,
                }}
              >
                Enter your 6-digit Bridge verification code sent to{' '}
                {mfaPhoneNumber}.
              </Typography>

              {hasError && (
                <HttpErrorMessage
                  message={
                    'The MFA code you entered is invalid. Please check your authenticator app or SMS and try again.'
                  }
                />
              )}

              <OTPInput
                value={otpValue}
                onChange={handleOnChangeOtpValue}
                length={6}
              />
              <Link
                component='button'
                variant='body2'
                disabled={loading || !!error}
                onClick={handleResendCode}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 'regular',
                  marginTop: 1,
                }}
              >
                Resend code
              </Link>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    paddingY: '24px',
                    fontSize: '16px',
                    lineHeight: '24px',
                    fontWeight: '400',
                    textAlign: 'center',
                  }}
                >
                  What phone number would you like to use?
                </Typography>
              </Box>
              <FormControl sx={{ width: '100%' }} variant='outlined'>
                <InputLabel required size='medium' id={`phone-number-label`}>
                  Phone number
                </InputLabel>
                <OutlinedInput
                  size='medium'
                  id='phone-number'
                  name='phone-number'
                  type={'text'}
                  label={'Phone number'}
                  error={!_.isNil(error)}
                  onChange={e => handleChangePhoneNumber(e.target.value)}
                  value={mfaPhoneNumber}
                  fullWidth
                  required
                />
                {!!error && (
                  <FormHelperText error id='phone-number-error'>
                    {error}
                  </FormHelperText>
                )}
              </FormControl>
            </>
          )}
        </Box>

        {!showSendCode && (
          <Button
            variant='contained'
            type='submit'
            color='secondary'
            sx={{ color: 'white', padding: '0.5rem', fontWeight: '500' }}
            loadingPosition='center'
            loading={loading}
            disabled={loading || !!error || !mfaPhoneNumber}
            onClick={handleSendCode}
            fullWidth
          >
            RECEIVE CODE
          </Button>
        )}
        <Button
          variant='text'
          sx={{ padding: '0.5rem', fontWeight: '500' }}
          onClick={goToPreviousStep}
          fullWidth
        >
          SELECT OTHER AUTHENTICATION METHOD
        </Button>
      </Paper>
    </Box>
  );
};
